/**
 * Sustainability Pledge page behaviour.
 *
 * Each feature is a small, independent init function that receives the page root,
 * so features can be removed or reordered without touching the others:
 *   - initStandards: expandable "full standard" panels
 *   - initNav:       sticky pillar nav with scroll-spy and smooth anchor scrolling
 *   - initCarousels: product carousels with prev/next controls
 *   - initHeroVideo: hero video that respects reduced-motion
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-ma-sp]';
  var READY_FLAG = 'maSpReady';

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function throttleToFrame(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  /* ------------------------------------------------------------------ */
  /* Expandable full standard                                            */
  /* ------------------------------------------------------------------ */

  function setPanelOpen(toggle, panel, open) {
    toggle.setAttribute('aria-expanded', String(open));
    panel.classList.toggle('is-open', open);
    // `inert` keeps collapsed content out of the tab order and the accessibility tree.
    panel.inert = !open;
  }

  function initStandards(root) {
    root.querySelectorAll('[data-ma-pillar]').forEach(function (pillar) {
      var toggle = pillar.querySelector('[data-ma-standard-toggle]');
      var panel = pillar.querySelector('[data-ma-standard-panel]');
      if (!toggle || !panel) return;

      setPanelOpen(toggle, panel, false);

      toggle.addEventListener('click', function () {
        var willOpen = toggle.getAttribute('aria-expanded') !== 'true';
        setPanelOpen(toggle, panel, willOpen);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky nav: scroll-spy + smooth anchors                             */
  /* ------------------------------------------------------------------ */

  function centreLinkInNav(list, link) {
    if (list.scrollWidth <= list.clientWidth) return;
    var left = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;
    list.scrollTo({ left: left, behavior: scrollBehavior() });
  }

  function initNav(root) {
    var nav = root.querySelector('[data-ma-nav]');
    if (!nav) return;

    var list = nav.querySelector('.ma-sp-nav__list');
    var links = Array.prototype.slice.call(nav.querySelectorAll('[data-ma-nav-link]'));
    var targets = links
      .map(function (link) {
        return document.getElementById(link.getAttribute('href').slice(1));
      })
      .filter(Boolean);

    function setActive(id) {
      links.forEach(function (link) {
        var isActive = link.getAttribute('href') === '#' + id;
        if (isActive) {
          link.setAttribute('aria-current', 'true');
          centreLinkInNav(list, link);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    nav.addEventListener('click', function (event) {
      var link = event.target.closest('[data-ma-nav-link]');
      if (!link) return;
      var target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      window.history.replaceState(null, '', link.getAttribute('href'));
    });

    if (!('IntersectionObserver' in window) || !targets.length) return;

    // Active pillar = the one crossing a band near the upper-middle of the viewport.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-35% 0px -60% 0px', threshold: 0 }
    );
    targets.forEach(function (target) {
      observer.observe(target);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Product carousels                                                   */
  /* ------------------------------------------------------------------ */

  function initCarousels(root) {
    root.querySelectorAll('[data-ma-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-ma-carousel-track]');
      var controls = carousel.querySelector('[data-ma-carousel-controls]');
      var prev = carousel.querySelector('[data-ma-carousel-prev]');
      var next = carousel.querySelector('[data-ma-carousel-next]');
      if (!track || !controls || !prev || !next) return;

      function updateControls() {
        var maxScroll = track.scrollWidth - track.clientWidth;
        controls.hidden = maxScroll <= 1;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= maxScroll - 1;
      }

      function scrollByPage(direction) {
        track.scrollBy({
          left: direction * track.clientWidth * 0.85,
          behavior: scrollBehavior()
        });
      }

      prev.addEventListener('click', function () {
        scrollByPage(-1);
      });
      next.addEventListener('click', function () {
        scrollByPage(1);
      });

      track.addEventListener('scroll', throttleToFrame(updateControls), { passive: true });
      window.addEventListener('resize', throttleToFrame(updateControls));

      if ('ResizeObserver' in window) {
        new ResizeObserver(throttleToFrame(updateControls)).observe(track);
      }

      updateControls();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Hero video                                                          */
  /* ------------------------------------------------------------------ */

  function initHeroVideo(root) {
    var video = root.querySelector('.ma-sp-hero__video');
    if (!video) return;

    if (prefersReducedMotion()) {
      video.removeAttribute('autoplay');
      video.pause();
      return;
    }

    var playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      // Autoplay can be blocked (e.g. low-power mode). The poster frame stays visible.
      playAttempt.catch(function () {});
    }
  }

  /* ------------------------------------------------------------------ */
  /* Bootstrapping                                                       */
  /* ------------------------------------------------------------------ */

  function initRoot(root) {
    if (root.dataset[READY_FLAG] === 'true') return;
    root.dataset[READY_FLAG] = 'true';

    // Enables the JS-only collapsed state for panels; without JS everything stays open.
    root.classList.add('ma-sp-js');

    initStandards(root);
    initNav(root);
    initCarousels(root);
    initHeroVideo(root);
  }

  function initAll(scope) {
    var context = scope || document;
    if (context.matches && context.matches(ROOT_SELECTOR)) {
      initRoot(context);
    }
    context.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
